/**
 * The wishlist page.
 *
 * Loads only on /pages/wishlist, so it can afford to do more than the product
 * button — chiefly checking what is actually in stock right now.
 *
 * Stock comes from Shopify's own product JSON rather than from our database.
 * We store what a fish was called when it was saved, not whether it is
 * available today, and a wishlist that says "in stock" about something sold out
 * three weeks ago is worse than one that says nothing.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-wishlist-page]');
  if (!root) return;

  var api = root.dataset.wishlistApi;
  var els = {
    status: root.querySelector('[data-wishlist-status]'),
    items: root.querySelector('[data-wishlist-items]'),
    empty: root.querySelector('[data-wishlist-empty]'),
    alerts: root.querySelector('[data-wishlist-alerts]'),
    form: root.querySelector('[data-wishlist-email-form]'),
    error: root.querySelector('[data-wishlist-email-error]'),
    confirmed: root.querySelector('[data-wishlist-email-confirmed]'),
    emailValue: root.querySelector('[data-wishlist-email-value]'),
    change: root.querySelector('[data-wishlist-email-change]'),
    prompt: root.querySelector('[data-wishlist-alerts-expand]'),
    popular: root.querySelector('[data-wishlist-popular]'),
    popularGroups: root.querySelector('[data-wishlist-popular-groups]'),
  };

  /**
   * The same browser identity the product buttons use.
   *
   * Reads localStorage directly rather than going through window.RAWishlist,
   * so this page does not depend on two deferred scripts executing in a
   * particular order. If wishlist.js has already run, both agree; if it has
   * not, this still finds the id it stored.
   */
  function getAnonId() {
    // Signed in, the list belongs to the account rather than to this browser.
    var customerKey = root.dataset.customerKey;
    if (customerKey && customerKey.length >= 16) return customerKey;

    // Defer to the shared helper rather than reimplementing the fallback. This
    // page once dropped straight to the browser id here while the rest of the
    // site was saving under the account, so the two disagreed about whose list
    // to show. One decision, made in one place.
    if (window.RAWishlist && typeof window.RAWishlist.identity === 'function') {
      return window.RAWishlist.identity();
    }
    if (window.RAWishlist && typeof window.RAWishlist.anonId === 'function') {
      return window.RAWishlist.anonId();
    }
    try {
      var existing = localStorage.getItem('ra_wishlist_id');
      if (existing) return existing;
      var fresh = (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2) + Date.now())
        .replace(/-/g, '')
        .slice(0, 32);
      localStorage.setItem('ra_wishlist_id', fresh);
      return fresh;
    } catch (e) {
      return null;
    }
  }

  var anonId = getAnonId();
  var state = { items: [], email: null, changing: false, expanded: false };

  /**
   * Product pages read this to decide whether they can honestly promise an
   * email. This page is the only one that knows for certain, so it is the only
   * one that writes it.
   */
  function rememberHasEmail(has) {
    try {
      if (has) localStorage.setItem('ra_wishlist_has_email', '1');
      else localStorage.removeItem('ra_wishlist_has_email');
    } catch (e) {
      /* nothing we can do */
    }
  }

  // --- Shopify stock lookup -------------------------------------------------

  var productCache = {};

  /** One request per product handle, however many variants are saved from it. */
  function loadProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      });
    return productCache[handle];
  }

  function decorateWithStock(items) {
    return Promise.all(
      items.map(function (item) {
        return loadProduct(item.handle).then(function (product) {
          if (!product) {
            // The product may have been deleted or unpublished since saving.
            item.available = null;
            return item;
          }
          var variant = null;
          for (var i = 0; i < product.variants.length; i++) {
            if (String(product.variants[i].id) === String(item.variantId)) {
              variant = product.variants[i];
              break;
            }
          }
          item.available = variant ? variant.available : null;
          item.price = variant ? variant.price : null;
          // Prefer the live title and image over whatever we recorded.
          item.title = variant
            ? product.title + (variant.title && variant.title !== 'Default Title' ? ' — ' + variant.title : '')
            : item.title;
          item.image = (variant && variant.featured_image && variant.featured_image.src) || product.featured_image || item.image;
          return item;
        });
      })
    );
  }

  // --- rendering ------------------------------------------------------------

  var money = function (pence) {
    if (pence === null || pence === undefined) return '';
    return '£' + (pence / 100).toFixed(2);
  };

  /**
   * Every wording, in one place, so nothing can drift out of step.
   *
   * Notifications default to on, which means a brand-new list would otherwise
   * claim "we'll email you" before anyone has given us an address. Only promise
   * an email when one can actually be sent; until then the row says what is
   * missing.
   */
  function notifyLabel(on, available) {
    if (!on) {
      return available === true
        ? 'Tell me if it sells out and comes back'
        : "Email me when it's back";
    }
    if (!state.email) return 'Alerts on — add your email below';
    return available === true
      ? "We'll email you if it sells out and comes back"
      : "We'll email you when it's back";
  }

  function itemMarkup(item) {
    var url = item.handle ? '/products/' + item.handle + '?variant=' + item.variantId : '#';

    // Reuses the theme's own inventory classes, so the green dot and colours
    // match the badges everywhere else on the site rather than being redrawn.
    var status;
    if (item.available === true) {
      status = '<span class="inventory inventory--high">In stock</span>';
    } else if (item.available === false) {
      status = '<span class="inventory inventory--out">Out of stock</span>';
    } else {
      status = '<span class="inventory inventory--out">No longer listed</span>';
    }

    var on = item.notify !== false;

    // Always rendered, so an item switched off still has a control to switch
    // back on — and worded for the actual case. For something in stock the
    // alert only fires if it sells out and returns, and saying "we'll email you
    // when it's back" about a fish that is sitting there is nonsense.
    var notifyRow =
      '<label class="wishlist-item__notify' + (on ? ' is-on' : '') + '">' +
      '<input type="checkbox" class="wishlist-item__notify-input" data-notify' + (on ? ' checked' : '') + '>' +
      '<span class="wishlist-item__notify-icon" aria-hidden="true">✉️</span>' +
      '<span class="wishlist-item__notify-text">' +
      notifyLabel(on, item.available) +
      '</span></label>';

    return (
      '<li class="wishlist-item" data-variant-id="' + escapeAttr(item.variantId) + '">' +
      '<a class="wishlist-item__image" href="' + escapeAttr(url) + '">' +
      (item.image ? '<img src="' + escapeAttr(item.image) + '" alt="" loading="lazy" width="80" height="80">' : '') +
      '</a>' +
      '<div class="wishlist-item__body">' +
      '<a class="wishlist-item__title" href="' + escapeAttr(url) + '">' + escapeHtml(item.title || 'View product') + '</a>' +
      '<div class="wishlist-item__meta">' + status +
      (item.price ? '<span class="wishlist-item__price">' + money(item.price) + '</span>' : '') +
      '</div>' +
      notifyRow +
      '</div>' +
      '<button type="button" class="wishlist-item__remove" data-remove aria-label="Remove from wishlist">Remove</button>' +
      '</li>'
    );
  }

  function render() {
    els.status.hidden = true;

    if (state.items.length === 0) {
      els.items.hidden = true;
      els.empty.hidden = false;
      els.alerts.hidden = true;
      return;
    }

    els.empty.hidden = true;
    els.items.hidden = false;
    els.items.innerHTML = state.items.map(itemMarkup).join('');

    var anyWatching = state.items.some(function (i) {
      return i.notify !== false;
    });

    // Always present once there is a list. Either it says where alerts go, or
    // it offers to start sending them - a shopper should never have to wonder
    // whether they set this up.
    els.alerts.hidden = false;

    // The form opens by itself only when something is already waiting on an
    // address. Otherwise it stays a single line until asked for, because a full
    // form demanding an email for a fish someone was only bookmarking is the
    // thing that made this annoying in the first place.
    if (anyWatching && !state.email) state.expanded = true;
    renderEmailState();
  }

  function renderEmailState() {
    var hasEmail = Boolean(state.email);
    // Changing an address puts the form back without discarding the one on
    // file, so cancelling can return to it.
    var showForm = (!hasEmail && state.expanded) || state.changing;
    var showPrompt = !hasEmail && !state.expanded && !state.changing;

    els.form.hidden = !showForm;
    els.confirmed.hidden = showForm || showPrompt;
    if (els.prompt) els.prompt.hidden = !showPrompt;
    if (hasEmail) els.emailValue.textContent = state.email;

    // Settled, this stops being a call to action and becomes a status line and
    // gets out of the way. While the form is up it is a panel again, and
    // dismissable.
    // Settled or merely offering, this is one quiet line; only an open form
    // earns the panel.
    els.alerts.classList.toggle('is-confirmed', !showForm);
  }

  // --- actions --------------------------------------------------------------

  function removeItem(variantId) {
    return fetch(api + '/api/items', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ anonId: anonId, variantId: variantId }),
    }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      state.items = state.items.filter(function (i) {
        return String(i.variantId) !== String(variantId);
      });
      // Keep the product page buttons in step.
      if (window.RAWishlist) {
        var ids = window.RAWishlist.cachedIds();
        ids.delete(String(variantId));
        window.RAWishlist.setCachedIds(ids);
        // Keeps the floating bubble's count honest when the last item is
        // removed from this page, where there is no product button to fire the
        // usual event.
        document.dispatchEvent(
          new CustomEvent('wishlist:changed', { detail: { count: ids.size, saved: false } })
        );
      }
      render();
    });
  }

  els.items.addEventListener('change', function (e) {
    var box = e.target.closest('[data-notify]');
    if (!box) return;
    var li = box.closest('.wishlist-item');
    var variantId = li.dataset.variantId;
    var wanted = box.checked;

    fetch(api + '/api/items/notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ anonId: anonId, variantId: variantId, notify: wanted }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        var item = state.items.filter(function (i) {
          return String(i.variantId) === String(variantId);
        })[0];
        if (item) item.notify = wanted;
        // The row states what is actually set up, rather than leaving the
        // customer to infer it from a tick.
        var row = li.querySelector('.wishlist-item__notify');
        var text = li.querySelector('.wishlist-item__notify-text');
        if (row) row.classList.toggle('is-on', wanted);
        if (text) text.textContent = notifyLabel(wanted, item && item.available);
        // Turning one on is only any use once we have somewhere to send it.
        if (wanted && !state.email) {
          els.alerts.hidden = false;
          els.form.querySelector('[name="email"]').focus();
        }
      })
      .catch(function (err) {
        console.error('[wishlist] could not change notification setting', err);
        box.checked = !wanted;
      });
  });

  els.items.addEventListener('click', function (e) {
    var button = e.target.closest('[data-remove]');
    if (!button) return;
    var li = button.closest('.wishlist-item');
    button.disabled = true;
    removeItem(li.dataset.variantId).catch(function () {
      button.disabled = false;
      button.textContent = 'Try again';
    });
  });

  els.form.addEventListener('submit', function (e) {
    e.preventDefault();
    els.error.hidden = true;

    var email = els.form.querySelector('[name="email"]').value.trim();
    var consent = els.form.querySelector('[name="consent"]').checked;
    var submit = els.form.querySelector('[data-wishlist-email-submit]');
    submit.disabled = true;

    fetch(api + '/api/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ anonId: anonId, email: email, consent: consent }),
    })
      .then(function (res) {
        return res.json().then(function (body) {
          if (!res.ok) throw new Error(body.error || 'HTTP ' + res.status);
          return body;
        });
      })
      .then(function (body) {
        state.email = body.email;
        state.changing = false;
        rememberHasEmail(true);
        // Every row was waiting on this, so they all need re-labelling.
        render();
      })
      .catch(function (err) {
        els.error.textContent = err.message || 'Sorry, that did not work. Please try again.';
        els.error.hidden = false;
      })
      .finally(function () {
        submit.disabled = false;
      });
  });

  if (els.prompt) {
    els.prompt.addEventListener('click', function () {
      state.expanded = true;
      renderEmailState();
      els.form.querySelector('[name="email"]').focus();
    });
  }

  var dismiss = root.querySelector('[data-wishlist-alerts-dismiss]');
  if (dismiss) {
    dismiss.addEventListener('click', function () {
      // Collapses back to the single line rather than hiding the section. There
      // is always something worth saying here, so nothing is ever dismissed
      // entirely - it just stops taking up room.
      state.changing = false;
      state.expanded = false;
      renderEmailState();
    });
  }

  els.change.addEventListener('click', function () {
    // Deliberately does not clear state.email. Clearing it made the form
    // reappear, but also convinced everything else on the page that no address
    // was on file - so abandoning the change looked like the address had been
    // wiped, and ticking a notification reopened the form from scratch.
    state.changing = true;
    renderEmailState();
    els.form.querySelector('[name="email"]').focus();
  });

  // --- what other customers have saved ---------------------------------------

  /**
   * Rendered from the API rather than from anything this browser knows, and
   * kept visually apart from the list above it. These are other people's saves,
   * and the one thing worse than not showing them is showing them somewhere
   * they could be mistaken for your own.
   *
   * No save or remove controls here on purpose: the moment this shelf has the
   * same buttons as the list above, the distinction stops being obvious.
   */
  function loadPopular() {
    if (!els.popular) return;

    fetch(api + '/api/popular')
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (data) {
        var groups = (data && data.groups) || [];
        if (groups.length === 0) return; // stays hidden

        // The heading states the window the figures actually cover, taken from
        // the API rather than written into the template - so the two cannot
        // disagree if the window is ever changed.
        var heading = root.querySelector('[data-wishlist-popular-heading]');
        if (heading && data.windowDays) {
          heading.textContent =
            'Most popular wishlisted items in the last ' + data.windowDays + ' days';
        }

        // Titles and images are re-read from Shopify rather than shown as
        // stored. What we hold is a snapshot from whenever somebody saved,
        // which by now includes titles mangled by a hand-pasted snippet long
        // since replaced - and would in any case go stale the moment a product
        // is renamed. The list above this does the same.
        return Promise.all(
          groups.map(function (group) {
            return Promise.all(group.items.map(freshenPopularItem)).then(function (items) {
              return { category: group.category, items: items };
            });
          })
        ).then(function (fresh) {
          els.popularGroups.innerHTML = fresh.map(popularGroupMarkup).join('');
          els.popular.hidden = false;
        });
      })
      .catch(function (err) {
        // A shelf that fails to load is not worth telling anyone about.
        console.error('[wishlist] could not load popular items', err);
      });
  }

  /** Live title, image and price, falling back to what was stored. */
  function freshenPopularItem(item) {
    return loadProduct(item.handle).then(function (product) {
      if (!product) return item;

      var variant = null;
      for (var i = 0; i < product.variants.length; i++) {
        if (String(product.variants[i].id) === String(item.variantId)) {
          variant = product.variants[i];
          break;
        }
      }

      item.title = product.title;
      item.price = variant ? variant.price : null;
      item.image =
        (variant && variant.featured_image && variant.featured_image.src) ||
        product.featured_image ||
        item.image;
      return item;
    });
  }

  function popularGroupMarkup(group) {
    var items = group.items
      .map(function (item) {
        var url = item.handle
          ? '/products/' + item.handle + (item.variantId ? '?variant=' + item.variantId : '')
          : '#';
        return (
          '<li class="wishlist-popular__item">' +
          '<a class="wishlist-popular__link" href="' + escapeAttr(url) + '">' +
          (item.image
            ? '<img class="wishlist-popular__image" src="' + escapeAttr(item.image) +
              '" alt="" loading="lazy" width="72" height="72">'
            : '<span class="wishlist-popular__image"></span>') +
          '<span class="wishlist-popular__title">' + escapeHtml(item.title || item.sku) + '</span>' +
          (item.price ? '<span class="wishlist-popular__price">' + money(item.price) + '</span>' : '') +
          // The API withholds the figure until it is worth showing, so this is
          // simply absent rather than needing its own judgement here.
          (item.people
            ? '<span class="wishlist-popular__count">Saved by ' + item.people + '</span>'
            : '') +
          '</a></li>'
        );
      })
      .join('');

    return (
      '<section class="wishlist-popular__group">' +
      '<h3 class="wishlist-popular__heading">' + escapeHtml(group.category) + '</h3>' +
      '<ul class="wishlist-popular__items">' + items + '</ul>' +
      '</section>'
    );
  }

  // --- boot -----------------------------------------------------------------

  if (!anonId) {
    console.error('[wishlist] no anon id: localStorage is unavailable', {
      hasGlobal: Boolean(window.RAWishlist),
    });
    els.status.textContent =
      'Your browser is blocking storage, so we cannot show your saved items.';
    return;
  }

  // A signed-in shopper's address is already known, so ask them to tick the
  // consent box rather than retype something we have.
  var knownEmail = root.dataset.customerEmail;
  if (knownEmail) {
    var field = els.form.querySelector('[name="email"]');
    if (field && !field.value) field.value = knownEmail;
  }

  /**
   * Fold a logged-out list into the account before reading it.
   *
   * Done here rather than relying on wishlist.js, and awaited rather than run
   * alongside: this page reads the account's list, and if the merge has not
   * finished first the customer sees an empty page and concludes their saved
   * fish are gone.
   *
   * The endpoint is idempotent, so calling it from both places is harmless.
   */
  function mergeThenLoad() {
    var customerKey = root.dataset.customerKey;
    if (!customerKey || customerKey.length < 16) return Promise.resolve();

    var browserId = null;
    try {
      browserId = localStorage.getItem('ra_wishlist_id');
    } catch (e) {
      return Promise.resolve();
    }
    if (!browserId || browserId === customerKey) return Promise.resolve();

    return fetch(api + '/api/merge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ anonId: browserId, customerKey: customerKey }),
    }).catch(function (err) {
      // A failed merge should not hide the account's own list.
      console.error('[wishlist] merge failed', err);
    });
  }

  loadPopular();

  mergeThenLoad()
    .then(function () {
      // no-store as well as the server saying so: a list that quietly omits
      // what someone just saved is the worst thing this page can do.
      return fetch(api + '/api/items?anonId=' + encodeURIComponent(anonId), {
        cache: 'no-store',
      });
    })
    .then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    })
    .then(function (data) {
      state.email = data.email || null;
      rememberHasEmail(Boolean(state.email));
      return decorateWithStock(data.items || []);
    })
    .then(function (items) {
      // Anything back in stock goes to the top — that is what someone opening
      // this page is hoping to find.
      items.sort(function (a, b) {
        if (a.available === b.available) return b.addedAt - a.addedAt;
        return a.available === true ? -1 : 1;
      });
      state.items = items;

      // This page has just been told, authoritatively, what is on the list.
      // The cached ids the bubble and the product buttons read from are only
      // ever written when something is saved or removed from this browser, so
      // they drift the moment the two disagree - a save from another device, a
      // row removed server-side, a save that failed after the cache was
      // written. The bubble then repeats a number nobody can explain and this
      // page contradicts it. Reconciling here means one visit puts it right.
      if (window.RAWishlist) {
        var live = new Set(
          items.map(function (i) {
            return String(i.variantId);
          })
        );
        window.RAWishlist.setCachedIds(live);
        document.dispatchEvent(
          new CustomEvent('wishlist:changed', { detail: { count: live.size, saved: live.size > 0 } })
        );
      }

      render();
    })
    .catch(function (err) {
      console.error('[wishlist] could not load the page', err);
      els.status.textContent = 'Sorry — we could not load your saved items. Please refresh.';
    });

  // --- helpers --------------------------------------------------------------

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function escapeAttr(s) {
    return escapeHtml(s);
  }
})();
